/**
 * LinkAIButton - Button to link an artifact to AI conversation
 * Part of link-based AI architecture - allows users to link documents, SSAs, diagrams to AI conversations
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui';
import { useAI } from '../contexts/AIContext';
import { logger } from '../utils/logger';
import type { ConversationLink } from '@aerotage/shared';

interface LinkAIButtonProps {
  artifactType: ConversationLink['artifactType'];
  artifactId: string;
  artifactName: string;
  projectId: string; // Required - for artifact scoping
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
}

// AI Link icon - chain link with sparkle
const LinkAIIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Link chain */}
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    {/* Sparkle */}
    <path d="M19 2l-1.5 4.5L13 8l4.5 1.5L19 14l1.5-4.5L25 8l-4.5-1.5L19 2" transform="translate(-3, 0) scale(0.5)" />
  </svg>
);

export default function LinkAIButton({ 
  artifactType, 
  artifactId, 
  artifactName,
  projectId,
  variant = 'outline',
  size = 'sm',
  className = ''
}: LinkAIButtonProps) {
  const [isLinking, setIsLinking] = useState(false);
  const { 
    linkArtifact, 
    createConversation,
    conversationLinks,
    activeConversationId,
  } = useAI();
  
  // Check if this artifact is already linked to current conversation
  const isLinked = conversationLinks.some(l => l.artifactId === artifactId);
  
  const handleLinkAI = useCallback(async () => {
    setIsLinking(true);
    
    try {
      // ALWAYS create a fresh conversation for clean context
      // This prevents context contamination from other documents/projects
      const newConversationId = await createConversation(`${artifactName} - AI Session`);
      
      // Link the artifact to the new conversation
      if (newConversationId) {
        await linkArtifact(artifactType, artifactId, artifactName, projectId, newConversationId);
        logger.debug('[LinkAIButton] Created new conversation with link:', newConversationId);
      }
    } catch (error) {
      logger.error('[LinkAIButton] Failed to link artifact:', error);
    } finally {
      setIsLinking(false);
    }
  }, [artifactType, artifactId, artifactName, projectId, linkArtifact, createConversation]);
  
  // If already linked: show status only (unlink via Stop Editing or close conversation tab)
  if (isLinked && activeConversationId) {
    return (
      <Button
        variant="info"
        size={size}
        className={`cursor-default ${className}`}
        disabled
        title="Linked to AI. Stop Editing or close conversation to unlink."
      >
        <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 11l3 3L22 4" />
        </svg>
        AI Linked
      </Button>
    );
  }
  
  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleLinkAI}
      disabled={isLinking}
      title={`Link ${artifactName} to AI conversation`}
    >
      {isLinking ? (
        <svg className="w-4 h-4 mr-1.5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" opacity="0.25" />
          <path d="M12 2a10 10 0 0 1 10 10" />
        </svg>
      ) : (
        <LinkAIIcon />
      )}
      <span className="ml-1.5">Link AI</span>
    </Button>
  );
}
